import React, { useMemo } from 'react'
import { DownloadCategoryChildModel, DownloadsCategoryModel, DownloadsParent } from '../../../models'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useDownloadFiles } from '../../../hooks/use-download-files'
import CategoryList from './CategoryList'
import { makeStyles } from 'tss-react/mui'
import MoveCategoryBreadcrumbs from './MoveDownloadItemBreadcrumbs'
import { ListSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { moveDownloadItem } from 'src/Libs/downloads/api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import useModals from 'src/Hooks/useModals'
import { DEEPEST_LAYER_TO_MOVE_CATEGORY, DEEPEST_LAYER_TO_MOVE_FILE } from 'src/Libs/downloads'

const useStyles = makeStyles()(theme => ({
  dialogContent: {
    paddingBottom: 0,
  },
}))

export interface MoveDownloadItemEditorProps {
  itemToMoveId: number
  onClose?: () => void
  selectCategory: (categoryId?: number) => void
  isFile?: boolean
}

const MoveCategoryDialog: React.FC<MoveDownloadItemEditorProps> = ({ itemToMoveId, onClose, selectCategory, isFile }) => {
  const { t } = useCustomTranslation()
  const { openConfirmationModal } = useModals()
  const authFetch = useAuthFetchWithResponse()
  const [parentItems, setParentItems] = React.useState<DownloadsParent[]>([])
  const [currentCategoryId, setCurrentCategoryId] = React.useState<number>(0)

  const [itemToMove, itemToMoveLoading] = useDownloadFiles(itemToMoveId, {}, isFile)
  const [category, categoryLoading] = useDownloadFiles(currentCategoryId, {
    type: 'category',
    forbiddenId: itemToMoveId,
  })

  const { classes } = useStyles()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()

  const categoryList = useMemo(() => {
    const children = category?.children ?? []
    const categories = children.filter(item => item.type === 'category')

    if (isFile) return categories
    if (!itemToMove?.deepestChildLayer || !category?.layer) return []
    const deepestPossibleLayer = itemToMove.deepestChildType === 'category' ? DEEPEST_LAYER_TO_MOVE_CATEGORY : DEEPEST_LAYER_TO_MOVE_FILE
    if (itemToMove.deepestChildLayer - itemToMove.layer + category?.layer >= deepestPossibleLayer) {
      return []
    }
    return categories
  }, [itemToMove, category, isFile])

  const onClickMoveButton = (): void => {
    if (currentCategoryId === 0) {
      onMoveItem()
      return
    }

    const currentParent = parentItems.at(-1)

    const itemPermissionsExceedParentPermissions =
      (itemToMove?.visibleToCustomer && !currentParent?.visibleToCustomer) || (itemToMove?.visibleToInstaller && !currentParent?.visibleToInstaller)

    if (itemPermissionsExceedParentPermissions) {
      const key = isFile ? 'edit-file' : 'edit-category'
      openConfirmationModal({
        title: t(`${key}.move.warning.title`),
        text: t(`${key}.move.warning.text`),
        buttonText: [t('common.cancel'), t(`${key}.move.button-action`)],
        onConfirm: () => {
          onMoveItem()
          onClose?.()
        },
      })
    } else {
      onMoveItem()
    }
  }

  const onMoveItem = (): void => {
    moveDownloadItem(itemToMoveId, currentCategoryId, authFetch)
      .then(result => {
        enqueueSuccessStack(t(`documents.${isFile ? 'edit-file' : 'edit-category'}.move.success`))
        onClose?.()
        dispatch(refreshApiView())
        selectCategory?.(currentCategoryId)
      })
      .catch(error => {
        const keyBase = `documents.${isFile ? 'edit-file' : 'edit-category'}.move`
        if (error.response?.data.errno) {
          enqueueErrorStack(t(`${keyBase}.error-${error.response.data.errno}`))
        } else {
          enqueueErrorStack(t(`${keyBase}.error-general`))
        }
      })
  }

  const onSelectCategoryToMove = (category: DownloadsCategoryModel | DownloadCategoryChildModel): void => {
    parentItems.push(category)
    setCurrentCategoryId(category.id)
  }

  const onNavigateBackCategory = (newActiveItemId: number): void => {
    setCurrentCategoryId(newActiveItemId)
    setParentItems(prev => {
      const idx = prev.findIndex(item => item.id === newActiveItemId)
      if (idx === -1) return [] // not found, clear
      return prev.slice(0, idx + 1)
    })
  }

  return (
    <Dialog
      title={isFile ? t('documents.edit-file.move.title') : t('documents.edit-category.move.title')}
      maxWidth="xs"
      onClose={onClose}
      contentClassName={classes.dialogContent}
      actions={{
        primary: {
          name: t(`documents.${isFile ? 'edit-file' : 'edit-category'}.move.button-action`),
          onClick: onClickMoveButton,
        },
        secondary: {
          name: t('common.cancel'),
          onClick: onClose,
        },
      }}
    >
      {categoryLoading && itemToMoveLoading ? (
        <ListSkeleton length={3} />
      ) : (
        <>
          <MoveCategoryBreadcrumbs parentCategories={parentItems} onClickCategory={onNavigateBackCategory} />
          <CategoryList categories={categoryList} onSelectCategory={onSelectCategoryToMove} />
        </>
      )}
    </Dialog>
  )
}

export default MoveCategoryDialog
